import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from "axios";
import SuccessModal from '../Modals/successModal/SuccessModal';
import './SetNewPassword.css';

const SetNewPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [requirements, setRequirements] = useState([
    { id: "special", label: "One Special Character", met: false },
    { id: "number", label: "One Number", met: false },
    { id: "uppercase", label: "One Uppercase character", met: false },
    { id: "length", label: "8 Character Min", met: false },
    { id: "lowercase", label: "One Lowercase character", met: false },
  ]);

  const email = location.state?.email;
  const otp = location.state?.code; 

  useEffect(() => {
    const updatedRequirements = [...requirements];
    updatedRequirements.find(req => req.id === "special").met = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    updatedRequirements.find(req => req.id === "number").met = /[0-9]/.test(password);
    updatedRequirements.find(req => req.id === "uppercase").met = /[A-Z]/.test(password);
    updatedRequirements.find(req => req.id === "length").met = password.length >= 8;
    updatedRequirements.find(req => req.id === "lowercase").met = /[a-z]/.test(password);
    setRequirements(updatedRequirements);
  }, [password]);

  const allRequirementsMet = requirements.every((req) => req.met);
  const passwordsMatch = password === confirmPassword && password !== "";
  const showConfirmPasswordError =
    confirmPasswordTouched && confirmPassword !== "" && password !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!email || !otp) {
      setErrorMessage("Invalid session. Restart password recovery.");
      return;
    }

    if (!allRequirementsMet || !passwordsMatch) return;
    setIsLoading(true);
    try {
      const res = await axios.post("/user/reset-password", {
        email,
        otp,
        password,
      });

      if (!res.data.successStatus) {
        setErrorMessage(res.data.message || "Unable to reset password.");
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      setSuccessModalVisible(true);
    } catch (err) {
      setErrorMessage(
        err.response?.data?.message || "Server error. Try again."
      );
      setIsLoading(false);
    }
  };


  const handleProceedToLogin = () => {
    setSuccessModalVisible(false);
    navigate("/login");
  };


  return (
    <div className="set-password-container">
      <div className="set-password-form">
        <div className="logo">NimbleBash</div>

        <div className="set-password-form-content">
          <h1>Set New Password</h1>
          <p className="subtitle">
            Set a new password, Kindly make sure it's a password you can remember.
          </p>

          {errorMessage && (
            <p style={{ color: "red", marginBottom: "10px" }}>{errorMessage}</p>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Create Password</label>
              <div className="input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => {
                    setPasswordTouched(true);
                    setIsPasswordFocused(true);
                  }}
                  onBlur={() => setIsPasswordFocused(false)}
                  placeholder="Enter Password"
                  className={passwordTouched && !allRequirementsMet ? "error" : ""}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>

              {isPasswordFocused && (
                <div className="requirements-container">
                  {requirements.map((req) => (
                    <div
                      key={req.id}
                      className={`requirement-chip ${
                        req.met
                          ? "requirement-met"
                          : passwordTouched
                          ? "requirement-not-met"
                          : ""
                      }`}
                    >
                      <span>{req.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <div className="input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onFocus={() => setConfirmPasswordTouched(true)}
                  placeholder="Enter Password"
                  className={showConfirmPasswordError ? "error" : ""}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? "🙈" : "👁️"}
                </button>
              </div>
              {showConfirmPasswordError && (
                <div className="error-text">Passwords do not match</div>
              )}
            </div>

            <div className="button-container">
              <button type="button" className="back-button" onClick={() => navigate(-1)}>
                Back
              </button>
              <button
                type="submit"
                className={`proceed-button ${
                  !allRequirementsMet || !passwordsMatch ? "disabled" : ""
                }`}
                disabled={!allRequirementsMet || !passwordsMatch || isLoading}
              >
                {isLoading ? "Processing..." : "Proceed"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <SuccessModal
        visible={successModalVisible}
        onClose={() => setSuccessModalVisible(false)}
        onProceed={handleProceedToLogin}
        title="Password Reset Successful"
        description="Your new password set up is successful. Kindly proceed to Login."
        buttonText="Go to Login"
        imagePath="/images/success-icon.png"
      />
    </div>
  );
};

export default SetNewPassword;
